import axios, { AxiosError, isAxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import { createDiscreteApi } from "naive-ui"
import { authService } from '@/services/AuthService'
import config from '@/services/ConfigService'

const { message, notification, loadingBar } = createDiscreteApi([ 'message', 'notification', 'loadingBar' ])

interface Options {
  showFeedback?: boolean
  successMessage?: string
  errorMessage?: string
}

let apiInstance: AxiosInstance | null = null

function getApi(): AxiosInstance {
  if (!apiInstance) {
    apiInstance = axios.create({
      baseURL: config.apiUrl + '/api'
    })

    // Set up interceptors once during creation
    apiInstance.interceptors.request.use(async (config) => {
      await handleAuth(config)
      return config
    }, (error) => {
      return Promise.reject(error)
    })

    apiInstance.interceptors.response.use((response) => {
      return response
    }, (error: AxiosError) => {
      console.log('Bad response:', error)

      if (isAxiosError(error)) {
        console.log('Cause', error.cause)
        console.log('Code', error.code)
        console.log('Response', error.response?.status)
        console.log('Message', error.message)

        if (error.response?.status === 401) {
          // User must sign in again
          authService.loginRedirect()
        }
        else {
          console.debug('Error is not 401')
          return Promise.reject(error)
        }
      }
      else {
        console.debug('Error is NOT axios')
        return Promise.reject(error)
      }
    })
  }
  
  return apiInstance
}

export async function checkHealth(): Promise<boolean> {
  try {
    const response = await fetch(config.apiUrl + '/healthz/operational')
    return response.ok
  }
  catch (error) {
    return false
  }
}

export async function useFetch(
  url: string,
  query?: any,
  {
    showFeedback = true,
    errorMessage = 'Something went wrong!'
  }: Options = {}
): Promise<any> {
  loadingBar.start()

  try {
    const { data } = await getApi().get(url, {
      params: query,
      paramsSerializer: {
        indexes: null
      }
    })

    return data
  } catch (error) {
    console.error(error)
    loadingBar.error()

    if (showFeedback) {
      if (isAxiosError(error)) {
        if (error.response?.data?.type === 'SqlException' && error.response?.data?.detail?.toLowerCase().includes('timeout')) {
          notification.error({
            title: 'Operation timeout',
            content: 'The request took too long to complete and has been terminated. Please try again by searching for a smaller data set.',
            duration: 10000
          })
        } else {
          message.error(errorMessage)
        }
      } else {
        message.error(errorMessage)
      }
    }

    throw error
  } finally {
    loadingBar.finish()
  }
}

export async function useUpdate (
  url: string,
  model: any,
  {
    showFeedback = true,
    successMessage = 'Changes were saved',
    errorMessage = 'Yikes! Changes were not saved'
  }: Options = {}
): Promise<any> {
  loadingBar.start()

  try {
    const { data } = await getApi().put(url, model)

    if (showFeedback) message.success(successMessage)
    return data
  } catch (error) {
    console.error(error)
    loadingBar.error()
    if (showFeedback) message.error(errorMessage)
    throw error
  } finally {
    loadingBar.finish()
  }
}

/**
 * 
 * @param url The URL to send the request to.
 * @param model Whatever model you want to send. Leave empty if nothing should be sent.
 * @param options Configure how the method behaves.
 * @returns 
 */
export async function usePatch(
  url: string,
  model?: any,
  params?: object,
  {
    showFeedback = true,
    successMessage = 'Changes were saved',
    errorMessage = 'Yikes! Changes were not saved'
  }: Options = {}
): Promise<any> {
  loadingBar.start()

  try {
    const { data } = await getApi().patch(url, model, {
      params,
      paramsSerializer: {
        indexes: null
      }
    })
    console.log(successMessage)
    if (showFeedback) message.success(successMessage)
    return data
  } catch (error) {
    console.error(error)
    loadingBar.error()
    if (showFeedback) message.error(errorMessage)
    throw error
  } finally {
    loadingBar.finish()
  }
}

export async function useCreate (
  url: string,
  model: unknown,
  {
    showFeedback = true,
    successMessage = 'Changes were saved',
    errorMessage = 'Yikes! Changes were not saved'
  }: Options = {}
) {
  loadingBar.start()

  try {
    const { data } = await getApi().post(url, model)

    if (showFeedback) message.success(successMessage)
    return data
  } catch (error) {
    console.error(error)
    loadingBar.error()
    if (showFeedback) message.error(errorMessage)
    throw error
  } finally {
    loadingBar.finish()
  }
}

export async function useDelete (
  url: string,
  {
    showFeedback = true,
    successMessage = 'Deletion successful',
    errorMessage = 'Deletion failed'
  }: Options = {}
) {
  loadingBar.start()

  try {
    const { data } = await getApi().delete(url)

    if (showFeedback) message.success(successMessage)
    return data
  } catch (error) {
    console.error(error)
    loadingBar.error()
    if (showFeedback) message.error(errorMessage)
    throw error
  } finally {
    loadingBar.finish()
  }
}

async function handleAuth(config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> {
  // Some routes do not need a token and should be filtered out of the interceptor
  if (config.url?.startsWith('cmd')) return config

  else {
    try {
      const token = await authService.getAccessToken()
      if (!token) throw new Error('No token aquired');
      config.headers.Authorization = `Bearer ${token}`
    } catch (error) {
      console.error('Error aquiring token:', error)
    }

    return config
  }
}

function parseJwt (token: string): any {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
  }).join(''));

  return JSON.parse(jsonPayload);
}

export function assignRoles(token: string): string[] {
  const parsed = parseJwt(token)

  return parsed.roles
}

// function isDateString(value: unknown): boolean {
//   const dateFormat = /^\d{4}-\d{2}-\d{2}T\d{2}.\d{2}.\d{2}Z/;
//   return typeof value === 'string' && dateFormat.test(value)
// }

// function convertToJSDate(data: unknown) {
//   if (data === null || data === undefined || typeof data !== 'object') {
//     return data
//   }

//   for (const [key, val] of Object.entries(data)) {
//     if (isDateString(val)) {
//       const converted = String(val).replace(/\./g, ':')
//       // @ts-expect-error
//       data[key] = Date.parse(converted)
//     }

//     else if (typeof val === 'object') {
//       convertToJSDate(val)
//     }
//   }

//   return data
// }

// function convertToDateString(data: unknown) {
//   if (data === null || data == undefined || typeof data !== 'object') {
//     return data
//   }

//   for (const [key, val] of Object.entries(data)) {
//     if (val instanceof Date) {
//       // @ts-expect-error
//       data[key] = val.toISOString()
//     }
//   }

//   return data
// }

// Export api as a getter
export const api = {
  get: (...args: Parameters<AxiosInstance['get']>) => getApi().get(...args),
  post: (...args: Parameters<AxiosInstance['post']>) => getApi().post(...args),
  put: (...args: Parameters<AxiosInstance['put']>) => getApi().put(...args),
  patch: (...args: Parameters<AxiosInstance['patch']>) => getApi().patch(...args),
  delete: (...args: Parameters<AxiosInstance['delete']>) => getApi().delete(...args),
}
